import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import {
  SatisfactionPeriod, QuestionId, QuestionPeriodSummary, SatisfactionPeriodView,
  MonthlyScore, ClinicScores, OpenFeedback,
  QUESTIONS, SATISFACTION_BY_PERIOD, SATISFACTION_TREND, SATISFACTION_BY_CLINIC,
  SATISFACTION_FEEDBACK, SATISFACTION_TARGET,
} from './mockData';
import { execApi } from '../../../services/api';
import './styles.css';

// Backend (dash_views.satisfaction_data) → mock-shaped types, so the JSX below is
// data-source agnostic and falls back to the seeded mock if the call fails.
type DrillData = {
  byPeriod: Record<SatisfactionPeriod, SatisfactionPeriodView>;
  trend: MonthlyScore[];
  byClinic: ClinicScores[];
  feedback: OpenFeedback[];
};

function normalizeDrill(raw: any): DrillData {
  const byPeriod = {} as Record<SatisfactionPeriod, SatisfactionPeriodView>;
  for (const [pid, pv] of Object.entries<any>(raw.by_period ?? {})) {
    const byQuestion = {} as Record<QuestionId, QuestionPeriodSummary>;
    for (const [qid, q] of Object.entries<any>(pv.by_question ?? {})) {
      byQuestion[qid as QuestionId] = {
        questionId: qid as QuestionId,
        avgScore: q.avg_score, responses: q.responses, deltaVsPrev: q.delta_vs_prev,
      };
    }
    byPeriod[pid as SatisfactionPeriod] = {
      label: pv.label, totalRespondents: pv.total_respondents, byQuestion,
      overall: pv.overall, overallDelta: pv.overall_delta,
    };
  }
  return {
    byPeriod,
    trend: (raw.trend ?? []).map((t: any) => ({
      month: t.month, psy: t.psy, wait: t.wait, cm: t.cm, nps: t.nps, open: t.open,
    })),
    byClinic: (raw.by_clinic ?? []).map((c: any) => ({
      clinic: c.clinic, responses: c.responses,
      psy: c.psy, wait: c.wait, cm: c.cm, nps: c.nps, open: c.open,
    })),
    feedback: (raw.feedback ?? []).map((f: any) => ({
      day: f.day, clinic: f.clinic, avgScore: f.avg_score, text: f.text,
    })),
  };
}

const PERIODS: { id: SatisfactionPeriod; label: string }[] = [
  { id: 'month-last',   label: 'חודש אחרון' },
  { id: 'quarter-last', label: 'רבעון אחרון' },
  { id: 'ytd',          label: 'YTD' },
  { id: 'year-2026',    label: 'שנתי 2026' },
];

const QCOLORS: Record<QuestionId, string> = {
  psy:  '#8B5CF6',
  wait: '#F59E0B',
  cm:   '#10B981',
  nps:  '#2563EB',
  open: '#DB2777',
};

const fmt1 = (n: number) => n.toFixed(2);
const fmt2 = (n: number) => n.toFixed(2);

export default function ExecSatisfaction() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<SatisfactionPeriod>('month-last');
  const [activeClinic, setActiveClinic] = useState<string | null>(null);
  const [activeQ, setActiveQ] = useState<QuestionId | null>(null);  // click a hero card → isolate that question in the trend
  const [drill, setDrill] = useState<DrillData | null>(null);

  useEffect(() => {
    let alive = true;
    execApi.getSatisfactionDrill()
      .then((res) => { if (alive) setDrill(normalizeDrill(res.data)); })
      .catch(() => { /* keep null → seeded mock fallback */ });
    return () => { alive = false; };
  }, []);

  // Real data when loaded, seeded mock as fallback so the screen never breaks.
  const byPeriod = drill?.byPeriod ?? SATISFACTION_BY_PERIOD;
  const trend = drill?.trend ?? SATISFACTION_TREND;
  const byClinic = drill?.byClinic ?? SATISFACTION_BY_CLINIC;
  // Comments are their own dataset, not a slice of the drill: 638 of them, and the
  // 362 that score a flat 10 bury the few worth reading. Paged and score-filtered
  // server-side — filtering a fetched page would only ever filter that page.
  const [band, setBand] = useState<'' | 'low' | 'mid' | 'high'>('');
  const [page, setPage] = useState(1);
  const [worstFirst, setWorstFirst] = useState(false);
  const [docFilter, setDocFilter] = useState('');
  const [cmFilter2, setCmFilter2] = useState('');
  const [exporting, setExporting] = useState(false);

  const exportFeedback = async () => {
    setExporting(true);
    try {
      const b = BANDS[band];
      const res = await execApi.getSatisfactionFeedbackExport({
        min_score: b.min, max_score: b.max, worst_first: worstFirst,
        doctor: docFilter || undefined, case_manager: cmFilter2 || undefined,
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const tag = docFilter || cmFilter2;
      a.download = `הערות-שביעות-רצון${tag ? '-' + tag : ''}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };
  const [fb, setFb] = useState<{ items: OpenFeedback[]; total: number;
    counts: Record<string, number>; doctors: string[]; caseManagers: string[] } | null>(null);
  const [fbLoading, setFbLoading] = useState(false);
  const PAGE_SIZE = 25;
  const BANDS: Record<string, { label: string; min?: number; max?: number }> = {
    '': { label: 'הכל' },
    low: { label: 'עד 5', max: 5 },
    mid: { label: '5 עד 8', min: 5, max: 8 },
    high: { label: '8 ומעלה', min: 8 },
  };

  useEffect(() => {
    let alive = true;
    setFbLoading(true);
    const b = BANDS[band];
    execApi.getSatisfactionFeedback({ page, page_size: PAGE_SIZE, min_score: b.min,
                                     max_score: b.max, worst_first: worstFirst,
                                     doctor: docFilter || undefined,
                                     case_manager: cmFilter2 || undefined })
      .then((res) => {
        if (!alive) return;
        setFb({
          items: (res.data.items ?? []).map((f: any) => ({
            day: f.day, clinic: f.clinic, avgScore: f.avg_score, text: f.text,
            psychiatrist: f.psychiatrist, caseManager: f.case_manager,
          })),
          total: res.data.total ?? 0,
          counts: res.data.score_counts ?? {},
          doctors: res.data.doctors ?? [],
          caseManagers: res.data.case_managers ?? [],
        });
      })
      .catch(() => { if (alive) setFb(null); })
      .finally(() => { if (alive) setFbLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [band, page, worstFirst, docFilter, cmFilter2]);

  const feedback = fb?.items ?? drill?.feedback ?? SATISFACTION_FEEDBACK;
  const fbTotal = fb?.total ?? feedback.length;
  const pages = Math.max(1, Math.ceil(fbTotal / PAGE_SIZE));
  const bandCount = (key: string) => {
    const c = fb?.counts ?? {};
    const inBand = (n: number) => {
      const b = BANDS[key];
      return (b.min === undefined || n >= b.min) && (b.max === undefined || n <= b.max);
    };
    return Object.entries(c).reduce((a, [k, v]) => a + (inBand(Number(k)) ? v : 0), 0);
  };

  const view = byPeriod[period];

  // Filter clinic scoping affects only the per-question hero cards and the
  // clinic table highlight. The trend + feedback list show full data.
  const clinicRow = useMemo(
    () => activeClinic ? byClinic.find((c) => c.clinic === activeClinic) : null,
    [activeClinic, byClinic],
  );

  return (
    <div className="sat-wrap">
      <div className="sat-header">
        <div className="sat-title-block">
          <button className="sat-back-btn" onClick={() => navigate('/exec')}>← חזרה</button>
          <h1 className="sat-title">דריל איכות שירות · שביעות רצון</h1>
        </div>
        <div className="sat-head-right">
          {/* The screen is called service quality and had four numbers and no answer.
              Coloured against the same target the rest of the screen uses. */}
          {view.overall != null && view.overall > 0 && (
            <div className={`sat-overall ${view.overall >= SATISFACTION_TARGET ? 'ok' : 'low'}`}>
              <span className="sat-overall-label">איכות שירות · ממוצע</span>
              <span className="sat-overall-value">
                {fmt1(view.overall)}
                {!!view.overallDelta && (
                  <span className={`sat-overall-delta ${view.overallDelta > 0 ? 'up' : 'down'}`}>
                    {view.overallDelta > 0 ? '▲' : '▼'} {fmt1(Math.abs(view.overallDelta))}
                  </span>
                )}
              </span>
            </div>
          )}
          <span className="sat-chip">{view.label} · {view.totalRespondents} משיבים</span>
        </div>
      </div>

      {/* Filters */}
      <div className="sat-filters">
        <div className="sat-filter">
          <label className="sat-filter-label">תקופה</label>
          <select
            className="sat-filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value as SatisfactionPeriod)}
          >
            {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div className="sat-filter">
          <label className="sat-filter-label">קליניקה</label>
          <select
            className={`sat-filter-select ${activeClinic ? 'has-value' : ''}`}
            value={activeClinic ?? ''}
            onChange={(e) => setActiveClinic(e.target.value || null)}
          >
            <option value="">הכל</option>
            {byClinic.map((c) => (
              <option key={c.clinic} value={c.clinic}>{c.clinic}</option>
            ))}
          </select>
        </div>
        {(activeClinic || period !== 'month-last') && (
          <button
            className="sat-filter-clear"
            onClick={() => { setActiveClinic(null); setPeriod('month-last'); }}
          >נקה הכל</button>
        )}
      </div>

      {/* 4 question hero cards */}
      <div className="sat-hero">
        {QUESTIONS.map((q) => {
          const baseSummary = view.byQuestion[q.id];
          const score = clinicRow ? (clinicRow as any)[q.id] as number : baseSummary.avgScore;
          const aboveTarget = score >= SATISFACTION_TARGET;
          const delta = baseSummary.deltaVsPrev;
          const deltaUp = delta > 0;
          return (
            <div key={q.id}
              className={`sat-q-card tone-${q.tone} ${aboveTarget ? 'good' : 'warn'} clickable${activeQ === q.id ? ' active' : ''}${activeQ && activeQ !== q.id ? ' dim' : ''}`}
              role="button" tabIndex={0}
              onClick={() => setActiveQ(activeQ === q.id ? null : q.id)}
              title={activeQ === q.id ? 'הצג את כל השאלות' : 'הצג רק שאלה זו בגרף'}>
              <div className="sat-q-label">{q.shortLabel}</div>
              <div className="sat-q-fulltext">{q.fullText}</div>
              <div className="sat-q-score-row">
                <span className="sat-q-score">{fmt2(score)}</span>
                <span className={`sat-q-delta ${deltaUp ? 'up' : 'down'}`}>
                  {deltaUp ? '↑' : '↓'} {Math.abs(delta).toFixed(2)}
                </span>
              </div>
              <div className="sat-q-meta">
                {clinicRow ? `${clinicRow.responses} משיבים · ${clinicRow.clinic}` : `${baseSummary.responses} משיבים · ${view.label}`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Unified container — trend + clinic table + feedback */}
      <div className="sat-unified">
        {/* Section 1 — Trend per question */}
        <div className="sat-unified-section">
          <div className="sat-section-head">
            <span className="sat-section-title">
              טרנד · 12 חודשים
              {activeQ && <span style={{ color: QCOLORS[activeQ], fontWeight: 700 }}> · {QUESTIONS.find((q) => q.id === activeQ)?.shortLabel}</span>}
            </span>
            <span className="sat-section-sub">
              {activeQ
                ? <>מציג רק את השאלה שבחרת · <button className="sat-q-clear" onClick={() => setActiveQ(null)}>הצג את כל השאלות</button></>
                : <>לחצו על כרטיס שאלה למעלה כדי לבודד אותה בגרף · קו אופקי בכתום ביעד {SATISFACTION_TARGET}</>}
            </span>
          </div>
          <div className="sat-trend-wrap">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend} margin={{ top: 10, right: 24, left: 8, bottom: 6 }}>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis domain={[6, 10]} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: '#101D35', border: '1px solid #243457',
                    borderRadius: 6, fontSize: 12, color: '#F1F5F9', direction: 'rtl',
                  }}
                  formatter={(v: number) => v.toFixed(2)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={SATISFACTION_TARGET} stroke="#F59E0B" strokeDasharray="3 3" label={{ value: `יעד ${SATISFACTION_TARGET}`, fill: '#F59E0B', fontSize: 11, position: 'insideTopLeft' }} />
                {QUESTIONS.filter((q) => !activeQ || q.id === activeQ).map((q) => (
                  <Line key={q.id}
                    type="monotone" dataKey={q.id} name={q.shortLabel}
                    stroke={QCOLORS[q.id]} strokeWidth={activeQ ? 3 : 2.5}
                    dot={{ r: activeQ ? 3 : 2 }} activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Section 2 — Per-clinic table */}
        <div className="sat-unified-section alt">
          <div className="sat-section-head">
            <span className="sat-section-title">פירוט פר קליניקה · ממוצע פר שאלה</span>
            <span className="sat-section-sub">תאים בכתום מתחת ליעד {SATISFACTION_TARGET}</span>
          </div>
          <table className="sat-clinic-table">
            <thead>
              <tr>
                <th>קליניקה</th>
                <th>משיבים</th>
                {QUESTIONS.map((q) => <th key={q.id}>{q.shortLabel}</th>)}
              </tr>
            </thead>
            <tbody>
              {byClinic.map((row) => (
                <tr key={row.clinic} className={activeClinic === row.clinic ? 'highlighted' : ''}>
                  <td className="sat-clinic-name">{row.clinic}</td>
                  <td>{row.responses}</td>
                  {QUESTIONS.map((q) => {
                    const score = (row as any)[q.id] as number;
                    return (
                      <td key={q.id} className={score < SATISFACTION_TARGET ? 'below-target' : ''}>
                        {fmt1(score)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Section 3 — Open feedback */}
        <div className="sat-unified-section">
          <div className="sat-section-head">
            <span className="sat-section-title">הערות פתוחות</span>
            <span className="sat-section-sub">ציטוט ישיר מ-<code>satisfaction_data</code> · הציון = ממוצע 4 המדדים של אותו משיב</span>
          </div>

          {/* The count sits on the chip, so you can see a band is worth opening before
              opening it — and an empty band reads as empty, not as a failed load. */}
          <div className="sat-fb-controls">
            <div className="sat-fb-bands">
              {(Object.keys(BANDS) as Array<keyof typeof BANDS>).map((k) => (
                <button
                  key={k || 'all'}
                  className={`sat-fb-chip ${band === k ? 'on' : ''} ${k}`}
                  onClick={() => { setBand(k as any); setPage(1);
                                   if (k === 'low') setWorstFirst(true); }}
                >
                  {BANDS[k].label}
                  {fb && <span className="sat-fb-chip-n">{k === '' ? fb.total && bandCount('') : bandCount(k as string)}</span>}
                </button>
              ))}
            </div>
            <label className="sat-fb-who-filter">
              רופא
              <select value={docFilter}
                      onChange={(e) => { setDocFilter(e.target.value); setPage(1); }}>
                <option value="">הכל</option>
                {(fb?.doctors ?? []).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="sat-fb-who-filter">
              מנהל/ת תיק
              <select value={cmFilter2}
                      onChange={(e) => { setCmFilter2(e.target.value); setPage(1); }}>
                <option value="">הכל</option>
                {(fb?.caseManagers ?? []).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            {/* Two orders, not a checkbox: "newest" and "worst" are both answers to
                "what should I read first", and the wording says so. */}
            <label className="sat-fb-who-filter">
              מיון
              <select value={worstFirst ? 'worst' : 'new'}
                      onChange={(e) => { setWorstFirst(e.target.value === 'worst'); setPage(1); }}>
                <option value="new">מהחדש לישן</option>
                <option value="worst">מהגרוע לטוב</option>
              </select>
            </label>
            <button className="sat-fb-export" onClick={exportFeedback} disabled={exporting}>
              {exporting ? 'מייצא…' : `⭳ ייצוא (${fbTotal.toLocaleString()})`}
            </button>
            <div className="sat-fb-pager">
              <span className="sat-fb-total">{fbTotal.toLocaleString()} תגובות</span>
              <button disabled={page <= 1 || fbLoading} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span className="sat-fb-page">{page} מתוך {pages}</span>
              <button disabled={page >= pages || fbLoading} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          </div>
          <div className="sat-feedback-list">
            {!fbLoading && feedback.length === 0 && (
              <div className="sat-fb-empty">אין תגובות בטווח הציונים הזה</div>
            )}
            {feedback.map((f, i) => (
              <div key={i} className={`sat-feedback-card ${f.avgScore < SATISFACTION_TARGET ? 'low' : 'high'}`}>
                <div className="sat-feedback-meta">
                  <span className="sat-feedback-score">{fmt1(f.avgScore)}</span>
                  <span className="sat-feedback-clinic">{f.clinic}</span>
                  <span className="sat-feedback-day">{f.day}</span>
                </div>
                <div className="sat-feedback-text">{f.text}</div>
                {(f.psychiatrist || f.caseManager) && (
                  <div className="sat-feedback-who">
                    {f.psychiatrist && <span>פסיכיאטר: <b>{f.psychiatrist}</b></span>}
                    {f.caseManager && <span>מנהל/ת תיק: <b>{f.caseManager}</b></span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
